import { useEffect, useRef } from 'react';
import { usePlayerStore } from '@/store/playerStore';
import { useMapStore } from '@/store/mapStore';
import { rankAndPick } from '@/features/smart-tune/scoring';
import { audioEngine } from '@/features/audio/audioEngine';
import { SMART_TUNE } from '@/config/product.config';

/**
 * Smart Tune controller — monitors map movement and the player state.
 *
 * When Smart Tune is enabled and the user pans/zooms the map meaningfully,
 * it scores visible stations and auto-switches playback to the best match.
 * Respects a manual-selection cooldown so user choices aren't overridden.
 */
export function SmartTuneController() {
  const smartTune = usePlayerStore((s) => s.smartTune);
  const setSmartTuneMessage = usePlayerStore((s) => s.setSmartTuneMessage);

  const mapInstance = useMapStore((s) => s.mapInstance);
  const visibleStations = useMapStore((s) => s.visibleStations);

  const lastCenterRef = useRef<[number, number] | null>(null);
  const lastZoomRef = useRef<number | null>(null);
  const lastManualAtRef = useRef<number>(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track manual selections to enforce cooldown
  const selectionSource = usePlayerStore((s) => s.selectionSource);
  const currentStationId = usePlayerStore((s) => s.currentStation?.id);

  useEffect(() => {
    if (selectionSource === 'manual') {
      lastManualAtRef.current = Date.now();
    }
  }, [selectionSource, currentStationId]);

  useEffect(() => {
    if (smartTune !== 'enabled') {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      return;
    }

    if (!mapInstance) return;

    const checkAndSwitch = () => {
      if (smartTune !== 'enabled') return;
      if (visibleStations.length === 0) return;

      // Enforce manual cooldown
      const sinceManual = Date.now() - lastManualAtRef.current;
      if (sinceManual < SMART_TUNE.manualCooldownMs) return;

      const center = mapInstance.getCenter();
      const zoom = mapInstance.getZoom();
      const mapCenter: [number, number] = [center.lng, center.lat];

      // Check for meaningful movement
      const lastCenter = lastCenterRef.current;
      const lastZoom = lastZoomRef.current;

      if (lastCenter && lastZoom !== null) {
        const km = haversineSimple(lastCenter, mapCenter);
        const zoomDelta = Math.abs(zoom - lastZoom);
        if (km < SMART_TUNE.movementThresholdKm && zoomDelta < SMART_TUNE.zoomDeltaThreshold) {
          return; // not enough movement
        }
      }

      lastCenterRef.current = mapCenter;
      lastZoomRef.current = zoom;

      // Score and pick
      const result = rankAndPick(visibleStations, {
        mapCenter,
        zoom,
        now: Date.now(),
        currentStationId: usePlayerStore.getState().currentStation?.id ?? null,
      });

      if (result.station && result.reason === 'switch') {
        // Don't switch to the same station
        if (result.station.id === usePlayerStore.getState().currentStation?.id) return;

        audioEngine.load(result.station, 'smart_tune');
        setSmartTuneMessage(
          `Smart Tune: ${result.station.name}${
            result.station.cityName ? ` — ${result.station.cityName}` : ''
          }`,
        );
      }
    };

    const onMoveEnd = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(checkAndSwitch, SMART_TUNE.debounceMs);
    };

    mapInstance.on('moveend', onMoveEnd);

    return () => {
      mapInstance.off('moveend', onMoveEnd);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [smartTune, mapInstance, visibleStations, setSmartTuneMessage]);

  return null;
}

// Lightweight haversine without importing the full geo utils
function haversineSimple(a: [number, number], b: [number, number]): number {
  const R = 6371;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
